/* An agent with no tables yet: what a space is, the templates as cards in one tray, and a
   box that hands a description to Definable, which designs the tables for the person to
   approve. */
import React, { useState } from "react";
import { ArrowUp, CalendarDays, Database, Headset, Plus, Table2, Target, UsersRound, type LucideIcon } from "lucide-react";

import { useSpaceTemplates } from "@/shared/api/spaces";
import Logo from "@/shared/components/Logo";

import { TemplateDialog } from "./SpaceDialogs";

import type { Agent, SpaceTemplate } from "@/shared/types";

const ICONS: Record<string, LucideIcon> = { leads: Target, support: Headset, bookings: CalendarDays };

export default function StartPage({ agent, onNewTable, onMade, onAskDefinable }: {
  agent: Agent;
  onNewTable: () => void;
  onMade: () => void;
  onAskDefinable?: (text: string) => void;
}) {
  const { data: templates } = useSpaceTemplates();
  const [picked, setPicked] = useState<SpaceTemplate | null>(null);
  const [asked, setAsked] = useState("");

  const ask = () => {
    if (!asked.trim() || !onAskDefinable) return;
    onAskDefinable(`Set up tables for ${agent.name}: ${asked.trim()}`);
  };

  return (
    <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
      <div className="min-h-full flex flex-col items-center justify-center px-6 py-10">
        <div className="w-12 h-12 rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-lift grid place-items-center text-[var(--text-secondary)] fade-in-up">
          <Database className="w-[22px] h-[22px]" strokeWidth={1.5} />
        </div>
        <h1 className="mt-3.5 mb-1.5 text-[28px] leading-[34px] font-medium tracking-[-0.025em] text-[var(--text-primary)] text-center">
          Data {agent.name} keeps
        </h1>
        <p className="m-0 max-w-[520px] text-center text-[14px] leading-[1.55] text-[var(--text-tertiary)]">
          {agent.name} reads and writes these tables. Rules like unique and required hold on every write, so a
          repeat or a missing field is refused, never saved.
        </p>
        <div className="mt-7 w-full max-w-[880px] rounded-[18px] bg-[var(--surface-2)] p-1">
          <div className="min-h-[34px] flex items-center px-3 pb-1 text-[12.5px] text-[var(--text-secondary)]">Start from a template</div>
          <div className="grid gap-1 sm:grid-cols-2 xl:grid-cols-4">
            {(templates ?? []).map((template) => {
              const Icon = ICONS[template.id] ?? Table2;
              return (
                <TemplateCard key={template.id} icon={Icon} title={template.name} description={template.description} tables={template.tables} onPick={() => setPicked(template)} />
              );
            })}
            <TemplateCard icon={Plus} title="Blank" description="Start empty. Add tables and columns yourself." tables={[]} onPick={onNewTable} />
          </div>
        </div>
        {onAskDefinable && (
          <div className="mt-4 w-full max-w-[880px] rounded-[18px] bg-[var(--surface-2)] p-1">
            <div className="flex items-center gap-2.5 rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-lift px-3 py-2.5">
              <Logo size={18} className="shrink-0 text-[var(--text-primary)]" />
              <input
                value={asked}
                onChange={(e) => setAsked(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder={`Describe what ${agent.name} should keep track of…`}
                aria-label="Describe the tables"
                className="flex-1 min-w-0 bg-transparent outline-none text-[14px] placeholder:text-[var(--text-quaternary)]"
              />
              <button
                type="button"
                aria-label="Ask Definable"
                onClick={ask}
                disabled={!asked.trim()}
                className="press w-[30px] h-[30px] shrink-0 grid place-items-center rounded-full bg-[var(--brand)] text-[var(--brand-foreground)] disabled:bg-[var(--disabled)]"
              >
                <ArrowUp className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            <div className="h-[33px] flex items-center px-3 text-[11.5px] text-[var(--text-tertiary)]">
              Definable designs the tables. You approve before anything is made.
            </div>
          </div>
        )}
      </div>
      {picked && <TemplateDialog agentId={agent.id} template={picked} onMade={onMade} onClose={() => setPicked(null)} />}
    </div>
  );
}

function TemplateCard({ icon: Icon, title, description, tables, onPick }: {
  icon: LucideIcon;
  title: string;
  description: string;
  tables: string[];
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="press card-hover text-left rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift p-4 flex flex-col hover:ring-[var(--hairline-focus)]"
    >
      <span className="w-8 h-8 rounded-[9px] bg-[var(--surface-2)] grid place-items-center text-[var(--text-secondary)] mb-2.5">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </span>
      <span className="text-[14px] font-medium text-[var(--text-primary)]">{title}</span>
      <span className="mt-1 min-h-[54px] text-[12.5px] leading-[1.45] text-[var(--text-tertiary)]">{description}</span>
      <span className="mt-3 pt-2.5 border-t hairline flex flex-col gap-1.5">
        {tables.length ? tables.map((name) => (
          <span key={name} className="flex items-center gap-1.5 text-[12.5px] text-[var(--text-secondary)]">
            <Table2 className="w-3.5 h-3.5 text-[var(--text-quaternary)]" strokeWidth={1.75} /> {name}
          </span>
        )) : <span className="text-[12.5px] text-[var(--text-quaternary)]">No tables yet</span>}
        <span className="flex items-center gap-1.5 text-[12.5px] text-[var(--text-secondary)]">
          <UsersRound className="w-3.5 h-3.5 text-[var(--text-quaternary)]" strokeWidth={1.75} /> People
          <span className="text-[var(--text-quaternary)]">· system</span>
        </span>
      </span>
    </button>
  );
}
